import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Locate, MapPin, Wifi, Zap, Star, X, Navigation, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import DirectionsMenu from "./DirectionsMenu";

export default function NearbyPanel({ 
  venues, 
  loading, 
  onClose, 
  userLocation,
  onLocate,
  gettingLocation 
}) {
  const navigate = useNavigate();
  const topVenues = venues.slice(0, 4);

  const getWifiIcon = (quality) => {
    const colors = {
      excellent: "text-green-500",
      good: "text-blue-500",
      fair: "text-amber-500",
      none: "text-gray-400"
    };
    return colors[quality] || "text-gray-400";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="absolute bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[1001]"
    >
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand to-brand-dark p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-full p-2">
                <Navigation className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Nearest Workspaces</h3>
                <p className="text-white/80 text-sm">Top 4 closest to you</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              data-testid="close-nearby-panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {!userLocation ? (
            <div className="text-center py-6">
              <div className="bg-brand/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Locate className="w-8 h-8 text-brand" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Enable Location</h4>
              <p className="text-sm text-gray-500 mb-4">
                Allow location access to find workspaces near you
              </p>
              <button
                onClick={onLocate}
                disabled={gettingLocation}
                className="bg-brand text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-dark transition-colors inline-flex items-center gap-2 disabled:opacity-70"
                data-testid="enable-location-btn"
              >
                {gettingLocation ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Getting Location...
                  </>
                ) : (
                  <>
                    <Locate className="w-4 h-4" />
                    Find My Location
                  </>
                )}
              </button>
            </div>
          ) : loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse flex gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : topVenues.length === 0 ? (
            <div className="text-center py-6">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">No Workspaces Found</h4>
              <p className="text-sm text-gray-500">
                Try expanding your search area
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {topVenues.map((venue, index) => (
                <motion.button
                  key={venue.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(`/venue/${venue.id}`)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                  data-testid={`nearby-venue-${index}`}
                >
                  {/* Rank Badge */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-amber-100 text-amber-700' :
                    index === 1 ? 'bg-gray-200 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>

                  {/* Venue Image */}
                  <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                    {venue.images?.[0] ? (
                      <img 
                        src={venue.images[0]} 
                        alt={venue.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-brand/20 to-accent/20 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-brand" />
                      </div>
                    )}
                    {venue.is_premium && (
                      <div className="absolute top-0 right-0 bg-amber-500 p-0.5 rounded-bl">
                        <Star className="w-2.5 h-2.5 text-white fill-white" />
                      </div>
                    )}
                  </div>

                  {/* Venue Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate group-hover:text-brand transition-colors">
                      {venue.name}
                    </h4>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Wifi className={`w-3 h-3 ${getWifiIcon(venue.amenities?.wifi_quality)}`} />
                        {venue.amenities?.wifi_quality || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-500" />
                        {venue.amenities?.power_outlets || 0}
                      </span>
                    </div>
                  </div>

                  {/* Distance & Directions */}
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <div className="text-brand font-semibold">
                      {venue.distance_km !== undefined ? `${venue.distance_km}km` : '--'}
                    </div>
                    <div 
                      onClick={(e) => e.stopPropagation()}
                      className="relative z-10"
                    >
                      <DirectionsMenu 
                        venue={venue} 
                        buttonSize="small"
                        buttonClassName="p-1.5 rounded-md bg-brand/10 text-brand hover:bg-brand hover:text-white transition-colors"
                      />
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {userLocation && topVenues.length > 0 && (
          <div className="border-t border-gray-100 p-3">
            <button
              onClick={() => navigate('/browse')}
              className="w-full text-center text-sm text-brand font-medium hover:text-brand-dark transition-colors py-2"
              data-testid="view-all-nearby"
            >
              View all nearby workspaces
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
